import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { editImg as EditImg } from 'theme/icon';
import confirm from 'theme/icon/confirm.png';
import cancel from 'theme/icon/cancel.png';
import { VelocityComponent, VelocityTransitionGroup } from 'velocity-react';
import styles from './styles.scss';
import useHook from '../hooks';
import Button from '../control/button';

import {
  TextAttribute,
  DateAttribute,
  DateRangeAttribute,
  CheckBoxAttribute,
  NumberAttribute,
  NumberRangeAttribute,
  EquationAttribute,
  SelectAttribute,
  MultipleChoiceAttribute,
} from './types';

const componentMap = {
  1: TextAttribute,
  2: NumberAttribute,
  3: SelectAttribute,
  4: CheckBoxAttribute,
  5: DateAttribute,
  6: DateRangeAttribute,
  7: NumberRangeAttribute,
  8: EquationAttribute,
  9: MultipleChoiceAttribute,
};

const Container = props => {
  const [isHovering, setIsHovering] = useState(false);
  const { isInEdit, item, handleSave, handleReset, handleChange, handleCancel, handleEdit } = useHook(props);
  const isChanged = props.meta.dirty && props.meta.touched;
  const isShowButtons = item.typeId !== 3 && item.typeId !== 8 && item.typeId !== 4 && !item.parentId && isInEdit;
  const isShowReset =
    item.typeId !== 4 && item.value && props.default.isEditAccess && !item.parentId && item.typeId !== 8 && !isInEdit;
  const isShowEdit = item.typeId !== 4 && props.default.isEditAccess && !isInEdit && item.typeId !== 8;
  const isShowHint = !isInEdit && isHovering && !item.value && item.typeId !== 4 && item.typeId !== 8;
  const delay = props.isDelay || props.isDelay === 0 ? 90 : 0;

  const getContainerStyle = () => {
    const style = {
      backgroundColor: '#FFFFFF',
      borderColor: '#FFFFFF',
    };

    // checkbox
    if (item.typeId === 4) {
      return style;
    }

    // formula
    if (item.typeId === 8 && item.error) {
      style.backgroundColor = '#FEE4E2';
      style.borderColor = '#EF5350';
    }

    if (isChanged || isInEdit) {
      style.backgroundColor = '#FFFBEF';
      style.borderColor = '#FFA037';
    }

    // multiple choice
    if (item.typeId === 9 && isInEdit && !isChanged) {
      style.backgroundColor = '#E5EFFF';
      style.borderColor = '#1B9CFB';
    }

    return style;
  };

  const Component = componentMap[item.typeId];
  return (
    <React.Fragment>
      <VelocityComponent animation={getContainerStyle()} duration={100} delay={delay}>
        <div
          style={{
            width: props.width,
            cursor: props.default.isEditAccess ? 'pointer' : 'unset',
          }}
          onClick={() => {
            props.onEdit();
            handleEdit();
          }}
          className={styles.attribute}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <Component
            {...item}
            default={props.default}
            meta={props.meta}
            isEditable={isInEdit}
            delay={delay}
            onChange={handleChange}
            onSave={handleSave}
            multipleChoiceId={props.multipleChoiceId}
          />
          <div className={styles.buttons}>
            <VelocityTransitionGroup
              enter={{
                animation: {
                  opacity: [1, 0],
                },
                duration: 100,
              }}
              leave={{
                animation: {
                  opacity: [0, 1],
                },
                duration: 100,
              }}
              style={{
                display: 'flex',
              }}
            >
              {isShowButtons && (
                <div className={styles.main}>
                  <Button
                    className={styles.save}
                    isShow={isInEdit}
                    disabled={(!props.meta.dirty && !props.meta.touched) || props.meta.error}
                    img={confirm}
                    onAction={e => {
                      handleSave();
                      e.stopPropagation();
                    }}
                  />
                  <Button
                    className={styles.cancel}
                    img={cancel}
                    onAction={e => {
                      props.onClose();
                      handleCancel();
                      e.stopPropagation();
                    }}
                    isShow={isInEdit}
                  />
                </div>
              )}
            </VelocityTransitionGroup>
            <VelocityTransitionGroup
              enter={{
                animation: {
                  opacity: [1, 0],
                },
                duration: 100,
              }}
              leave={{
                animation: {
                  opacity: [0, 1],
                },
                duration: 100,
              }}
            >
              {isHovering && (isShowEdit || isShowReset) && (
                <div>
                  {isShowEdit && <EditImg className={styles.edit} alt="" />}
                  {isShowReset && <Button className={styles.delete} text="Cбросить" onAction={handleReset} />}
                </div>
              )}
            </VelocityTransitionGroup>
          </div>
          <div className={styles.hint}>
            <VelocityTransitionGroup
              enter={{
                animation: {
                  opacity: [1, 0],
                },
                duration: 100,
              }}
              leave={{
                animation: {
                  opacity: [0, 1],
                },
                duration: 100,
              }}
            >
              {isShowHint && <span>Нажмите на карточку, чтобы редактировать</span>}
            </VelocityTransitionGroup>
          </div>
        </div>
      </VelocityComponent>
    </React.Fragment>
  );
};

Container.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.objectOf(PropTypes.any),
    onChange: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  meta: PropTypes.objectOf(PropTypes.any).isRequired,
  default: PropTypes.objectOf(PropTypes.any).isRequired,
  isChanged: PropTypes.bool.isRequired,
  isShowArrow: PropTypes.bool.isRequired,
  isInEdit: PropTypes.bool.isRequired,
  isDelay: PropTypes.bool.isRequired,
  isShowSave: PropTypes.bool.isRequired,
  isEditAccess: PropTypes.bool.isRequired,
  isShowCancel: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  multipleChoiceId: PropTypes.number,
  width: PropTypes.number,
};

Container.defaultProps = {
  multipleChoiceId: null,
  width: 335,
};

export default Container;
